import {forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState} from 'react'
import _ from 'lodash'
import {useMeasure} from 'react-use'
import {Align, FixedSizeGrid, GridItemKeySelector, GridOnScrollProps} from 'react-window'
import {Dimmer, Loader} from 'semantic-ui-react'
import {FaArrowUp} from 'react-icons/fa'
import {IMediaListItemProps, MediaListItem} from './item'
import {IDataSourceItem} from '@/features/data-sources'
import {UseMeasureRect} from 'react-use/lib/useMeasure'
import './style.css'

export type {IMediaListItemProps}
export {MediaListItem}

export interface IGridState<T extends IDataSourceItem> {
  rowCount: number
  rowHeight: number
  columnCount: number
  columnWidth: number
  items: IMediaListProps<T>['items']
  itemProps: IMediaListProps<T>['itemProps']
  viewMode: IMediaListItemProps<T>['viewMode']
}

export interface IMediaListRef<T extends IDataSourceItem> {
  scrollToTop: () => void
  scrollToItem: (item: T, align?: Align) => void
}

export interface IMediaListProps<T extends IDataSourceItem> {
  listRef?: React.RefObject<IMediaListRef<T> | null>
  items: T[]
  itemProps?:
    | Partial<IMediaListItemProps<T>>
    | ((item: T, index: number) => Partial<IMediaListItemProps<T>>)
  viewMode?: IMediaListItemProps<T>['viewMode']
  listRowHeight?: number
  idealColumnWidth?: number
  itemAspectRatio?: number
  isBusy?: boolean
  onSizeChanged?: (size: UseMeasureRect) => void
}

const outerElementVertical = forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  (props, ref) => (
    <div
      ref={ref}
      {...props}
      style={{
        ...props.style,
        overflowX: 'hidden',
      }}
    />
  )
)

const outerElementHorizontal = forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  (props, ref) => (
    <div
      ref={ref}
      {...props}
      style={{
        ...props.style,
        overflowY: 'hidden',
      }}
    />
  )
)

export function MediaList<T extends IDataSourceItem>(props: IMediaListProps<T>) {
  const {
    listRef,
    items,
    itemProps,
    listRowHeight,
    idealColumnWidth,
    itemAspectRatio,
    viewMode,
    isBusy,
    onSizeChanged,
  } = {
    listRowHeight: 30,
    idealColumnWidth: 150,
    itemAspectRatio: 8 / 12,
    ...props,
  }

  const [measureRef, size] = useMeasure<HTMLDivElement>()
  const gridRef = useRef<FixedSizeGrid<IGridState<T>>>(null)
  const scrollPanelRef = useRef<HTMLDivElement>(null)
  const [scrollPosition, setScrollPosition] = useState<{
    top: number
    left: number
  }>({
    top: 0,
    left: 0,
  })

  useImperativeHandle(listRef, () => {
    return {
      scrollToTop,
      scrollToItem,
    }
  })

  useEffect(() => {
    if (!onSizeChanged) return
    onSizeChanged(size)
  }, [size])

  const gridState = useMemo<IGridState<T>>(() => {
    const scrollWidth = Math.max(
      (scrollPanelRef.current?.offsetWidth || 0) - (scrollPanelRef.current?.clientWidth || 0)
    )
    const scrollPanelWidth = size.width - scrollWidth
    const scrollHeight = Math.max(
      (scrollPanelRef.current?.offsetHeight || 0) - (scrollPanelRef.current?.clientHeight || 0),
      10
    )
    const scrollPanelHeight = size.height - scrollHeight
    if (viewMode === 'list') {
      const columnCount = 1
      const rowCount = items.length
      const columnWidth = scrollPanelWidth
      const rowHeight = listRowHeight
      return {
        columnCount,
        rowCount,
        columnWidth,
        rowHeight,
        items,
        itemProps,
        viewMode,
      }
    } else if (viewMode === 'single-row') {
      const rowCount = 1
      const columnCount = items.length
      const rowHeight = scrollPanelHeight
      const columnWidth = rowHeight * (itemAspectRatio || 1)
      return {
        columnCount,
        rowCount,
        columnWidth,
        rowHeight,
        items,
        itemProps,
        viewMode,
      }
    } // grid
    else {
      const columnCount = Math.max(1, Math.round(size.width / idealColumnWidth))
      const rowCount = Math.max(Math.ceil(items.length / (columnCount || 1)), 1)
      const columnWidth = scrollPanelWidth / columnCount
      const rowHeight = columnWidth / (itemAspectRatio || 1)
      return {
        columnCount,
        rowCount,
        columnWidth,
        rowHeight,
        items,
        itemProps,
        viewMode,
      }
    }
  }, [items, itemProps, size, idealColumnWidth, itemAspectRatio, viewMode])

  function scrollToTop() {
    if (!gridRef.current) return
    gridRef.current.scrollToItem({
      rowIndex: 0,
      columnIndex: 0,
    })
  }

  function scrollToItem(item: T, align: Align = 'smart') {
    if (!gridRef.current) return
    const index = _.findIndex(items, (i) => i._id === item._id)
    if (index === -1) {
      return gridRef.current.scrollToItem({
        rowIndex: 0,
        columnIndex: 0,
      })
    }
    const row = Math.floor(index / gridState.columnCount)
    const col = index - row * gridState.columnCount
    gridRef.current.scrollToItem({
      rowIndex: row,
      columnIndex: col,
      align,
    })
  }

  function handleOnScroll(props: GridOnScrollProps) {
    setScrollPosition({
      top: props.scrollTop,
      left: props.scrollLeft,
    })
  }

  const getItemKey: GridItemKeySelector<IGridState<T>> = ({columnIndex, rowIndex, data}) => {
    const {columnCount, items} = data
    const index = rowIndex * columnCount + columnIndex
    const item = items[index]
    if (item) return item._id
    return index
  }

  const outerElement = viewMode === 'single-row' ? outerElementHorizontal : outerElementVertical

  return (
    <div
      className='media-list'
      ref={measureRef}
    >
      <div
        className='scroll-top'
        onClick={() => gridRef.current?.scrollTo({scrollTop: 0})}
        style={
          scrollPosition.top < 200 ?
            {
              opacity: 0,
              pointerEvents: 'none',
            }
          : undefined
        }
      >
        <FaArrowUp />
        scroll to top
      </div>
      <FixedSizeGrid<IGridState<T>>
        style={{
          scrollbarGutter: 'stable',
        }}
        ref={gridRef}
        outerRef={scrollPanelRef}
        outerElementType={outerElement}
        overscanRowCount={3}
        columnCount={gridState.columnCount}
        rowCount={gridState.rowCount}
        columnWidth={gridState.columnWidth}
        rowHeight={gridState.rowHeight}
        width={size.width}
        height={size.height}
        itemData={gridState}
        onScroll={handleOnScroll}
        itemKey={getItemKey}
        children={MediaListItem}
      />
      <Dimmer active={isBusy}>
        <Loader />
      </Dimmer>
    </div>
  )
}
